import {ArrowRightOutlined, CheckCircleFilled, ScanOutlined, SyncOutlined, WarningFilled} from "@ant-design/icons";
import {Button, Row, Tag, Tooltip} from "antd";
import {useCallback, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSetRecoilState} from "recoil";
import {globalNotificationState} from "../../../_common/global_state";
import {apiCreateLivenessLink, apiGetLivenessResult} from "../../../api/restful/verification";

/**
 * When to ask for the verdict. The capture runs in another tab, so what matters
 * is the user coming BACK — not polling while they are away. The webhook can
 * trail their return by a beat, hence a short burst rather than one call.
 */
const RETURN_CHECK_DELAYS_MS = [0, 2500, 6000];

/** Focus and visibilitychange both fire on a tab switch — only run one burst. */
const RETURN_DEBOUNCE_MS = 1500;

/** Posted by the capture tab to its opener on a terminal state. */
const HOST_RETURN_MESSAGE_TYPE = "kyc-verification-complete/v1";

/** UUID v4 for the idempotency key. crypto.randomUUID needs a secure context. */
const newIdempotencyKey = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

/**
 * Remembers an in-flight attempt across reloads. The field value is only written
 * once ACCEPTED, so without this a refresh mid-capture shows "Start" and burns a
 * second link while the first is live.
 */
const attemptKey = (historyId, fieldSettingId) => `liveness_attempt_${historyId}_${fieldSettingId}`;

const readAttempt = (historyId, fieldSettingId) => {
    try {
        const raw = localStorage.getItem(attemptKey(historyId, fieldSettingId));
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const writeAttempt = (historyId, fieldSettingId, data) => {
    try {
        localStorage.setItem(attemptKey(historyId, fieldSettingId), JSON.stringify(data));
    } catch {
        /* private mode / quota — the flow still works, it just won't survive a reload */
    }
};

const clearAttempt = (historyId, fieldSettingId) => {
    try {
        localStorage.removeItem(attemptKey(historyId, fieldSettingId));
    } catch {
        /* ignore */
    }
};

/** Failure envelopes are HTTP 400 with `status: "failed"`; errObj is a string here. */
const errText = (res, fallback) => {
    const e = res?.errObj;
    if (typeof e === "string" && e) return e;
    if (e && typeof e === "object") return e.message || e.code || fallback;
    return fallback;
};

/**
 * Turn a terminal-but-not-accepted result into something a user can act on.
 * `invalidated` means an operator changed the policy mid-capture.
 *
 * Takes `t` because the caller stores the finished string in state.
 */
const failureMessage = (result, t) => {
    if (result?.eventType === "verification_link.expired") {
        return t("accountVerification.liveness.failure.linkExpired", {
            defaultValue: "The link expired before it was opened. Start a new check.",
        });
    }
    switch (result?.status) {
        case "invalidated":
            return t("accountVerification.liveness.failure.invalidated", {
                defaultValue: "Verification was interrupted. Please try again.",
            });
        case "expired":
            return t("accountVerification.liveness.failure.expired", {
                defaultValue: "The session timed out. Please try again.",
            });
        case "completed":
            return result?.outcome === "failed"
                ? t("accountVerification.liveness.failure.notProcessed", {
                    defaultValue: "We couldn't process the capture. Please try again.",
                })
                : t("accountVerification.liveness.failure.noMatch", {
                    defaultValue: "We couldn't confirm it was you. Please try again.",
                });
        default:
            return t("accountVerification.liveness.failure.incomplete", {
                defaultValue: "The check did not complete. Please try again.",
            });
    }
};

export const LivenessField = ({
                                  historyId,
                                  documentSettingId,
                                  isRequired,
                                  fieldSettingId,
                                  label,
                                  description,
                                  fieldPath,
                                  form,
                                  handleAutoSave,
                                  layout,
                                  readOnly,
                              }) => {
    const setNotificationMessage = useSetRecoilState(globalNotificationState);
    const {t} = useTranslation();

    // "checking" | "idle" | "creating" | "awaiting" | "accepted" | "failed" | "stalled"
    const [phase, setPhase] = useState("checking");
    const [failure, setFailure] = useState("");
    const [manualChecking, setManualChecking] = useState(false);
    /** True once a link has been created and the attempt hasn't been resolved. */
    const [hasAttempt, setHasAttempt] = useState(false);
    /** The live link, kept only when the browser refused the capture tab. */
    const [popupBlockedUrl, setPopupBlockedUrl] = useState("");

    /** Pending checks from the current burst. */
    const checkTimers = useRef([]);
    const lastBurstAt = useRef(0);
    const mounted = useRef(true);

    const savedValue = form?.getFieldValue(fieldPath);

    const stopChecks = useCallback(() => {
        checkTimers.current.forEach(clearTimeout);
        checkTimers.current = [];
    }, []);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            stopChecks();
        };
    }, [stopChecks]);

    /**
     * Commit a pass. The backend overwrites whatever we send with the accepted
     * attempt's sessionId anyway, but sending it keeps the draft honest.
     */
    const commitAccepted = useCallback((sessionId) => {
        stopChecks();
        clearAttempt(historyId, fieldSettingId);
        if (!mounted.current) return;
        setFailure("");
        setHasAttempt(false);
        setPopupBlockedUrl("");
        setPhase("accepted");
        if (form && fieldPath && sessionId) {
            form.setFieldValue(fieldPath, sessionId);
            handleAutoSave?.();
        }
    }, [form, fieldPath, handleAutoSave, stopChecks, historyId, fieldSettingId]);

    /**
     * One poll. Returns true when the result is terminal so callers can stop.
     * `found: false` is in-progress, not an error.
     */
    const readResult = useCallback(async () => {
        const res = await apiGetLivenessResult({historyId, fieldSettingId});

        if (res?.status === "failed") {
            // A transient read failure shouldn't kill the attempt — keep polling.
            console.error("liveness result/get failed:", errText(res, "unknown error"));
            return false;
        }

        const data = res?.data;
        if (!data?.found) return false;
        if (!data.terminal) return false;

        if (data.accepted) {
            commitAccepted(data.sessionId);
            return true;
        }

        // Terminal but NOT accepted — only report it if it is OUR attempt. An
        // abandoned earlier attempt can land its webhook mid-run, and reporting
        // that would fail the user before they have finished. (Accepted is exempt
        // above: passing is permanent whichever attempt earned it.)
        const mine = readAttempt(historyId, fieldSettingId)?.verificationId;
        if (mine && data.verificationId && data.verificationId !== mine) return false;

        // The attempt is spent, so a retry must create a brand new link.
        stopChecks();
        clearAttempt(historyId, fieldSettingId);
        if (mounted.current) {
            setHasAttempt(false);
            setPopupBlockedUrl("");
            setFailure(failureMessage(data, t));
            setPhase("failed");
        }
        return true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historyId, fieldSettingId, commitAccepted, stopChecks]);

    /**
     * One burst of checks: now, then twice more to cover webhook lag. Stops at
     * the first terminal verdict; if none arrives the field goes to "stalled",
     * which keeps the "Check status" button on screen.
     */
    const runChecks = useCallback(() => {
        stopChecks();
        lastBurstAt.current = Date.now();
        setPhase("awaiting");

        RETURN_CHECK_DELAYS_MS.forEach((delay, i) => {
            const last = i === RETURN_CHECK_DELAYS_MS.length - 1;
            checkTimers.current.push(setTimeout(async () => {
                if (!mounted.current) return;
                try {
                    const terminal = await readResult();
                    if (terminal) stopChecks();
                    else if (last && mounted.current) setPhase("stalled");
                } catch (e) {
                    console.error("liveness check error", e);
                    if (last && mounted.current) setPhase("stalled");
                }
            }, delay));
        });
    }, [readResult, stopChecks]);

    /**
     * The capture runs in another tab, so coming back to this one is the signal
     * that something may have changed. Only while an attempt is open — an
     * untouched or already-resolved field has nothing to ask about.
     */
    useEffect(() => {
        if (!hasAttempt) return undefined;

        const onReturn = () => {
            if (document.visibilityState !== "visible") return;
            // focus and visibilitychange both fire on one tab switch.
            if (Date.now() - lastBurstAt.current < RETURN_DEBOUNCE_MS) return;
            runChecks();
        };

        /**
         * The capture tab's completion signal — faster than waiting for focus.
         * Trusted only when the origin we opened AND the message type match:
         * any page can post to this window.
         */
        const onMessage = (event) => {
            const expected = readAttempt(historyId, fieldSettingId)?.origin;
            if (!expected || event.origin !== expected) return;
            if (event.data?.type !== HOST_RETURN_MESSAGE_TYPE) return;
            runChecks();
        };

        document.addEventListener("visibilitychange", onReturn);
        window.addEventListener("focus", onReturn);
        window.addEventListener("message", onMessage);
        return () => {
            document.removeEventListener("visibilitychange", onReturn);
            window.removeEventListener("focus", onReturn);
            window.removeEventListener("message", onMessage);
        };
    }, [hasAttempt, runChecks, historyId, fieldSettingId]);

    // On mount: has this field already passed? An accepted attempt is permanent —
    // the backend matches any accepted attempt for the (history, field) pair with
    // no recency constraint, and link-create refuses to issue a new link once so.
    useEffect(() => {
        let cancelled = false;

        (async () => {
            if (!historyId || !fieldSettingId) {
                setPhase("idle");
                return;
            }
            try {
                const res = await apiGetLivenessResult({historyId, fieldSettingId});
                if (cancelled || !mounted.current) return;

                const data = res?.status === "failed" ? null : res?.data;

                if (data?.found && data.terminal && data.accepted) {
                    commitAccepted(data.sessionId);
                    return;
                }
                if (data?.found && data.terminal) {
                    clearAttempt(historyId, fieldSettingId);
                    setFailure(failureMessage(data, t));
                    setPhase("failed");
                    return;
                }

                // Nothing terminal yet. If a link was created earlier (possibly in a
                // previous page load), the attempt is still live — keep watching for
                // it rather than offering a Start that would issue a second link.
                // No burst here: this mount check WAS the check. The next one happens
                // when the user comes back from the capture tab.
                const attempt = readAttempt(historyId, fieldSettingId);
                if (attempt || savedValue) {
                    setHasAttempt(true);
                    setPhase("awaiting");
                    return;
                }
                setPhase("idle");
            } catch (e) {
                console.error("liveness initial check failed", e);
                if (!cancelled && mounted.current) setPhase("idle");
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historyId, fieldSettingId]);

    /**
     * Claim the tab inside the click, before any await. Safari only honours
     * window.open in the synchronous part of a gesture — opening it after the
     * link-create resolves is silently dropped by "Block Pop-ups", leaving the
     * user on a permanent "Waiting" with the link already burned.
     * Null when even this was refused.
     */
    const openCaptureWindow = () => {
        const win = window.open("", "_blank");
        if (!win) return null;
        try {
            // A bare about:blank for the length of the create call reads as broken.
            win.document.write('<!doctype html><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><body style="margin:0;padding:24px;font:14px/1.5 system-ui,-apple-system,sans-serif;color:#555"></body>');
            win.document.close();
            // textContent, not markup: the copy comes from a translation file.
            win.document.title = t("accountVerification.liveness.title", {defaultValue: "Liveness check"});
            if (win.document.body) {
                win.document.body.textContent = t("accountVerification.liveness.preparing", {
                    defaultValue: "Preparing your verification…",
                });
            }
        } catch {
            /* never fail the flow over a placeholder */
        }
        return win;
    };

    const handleStart = async () => {
        if (!historyId || !documentSettingId || !fieldSettingId) return;

        const captureWindow = openCaptureWindow();

        setPopupBlockedUrl("");
        setPhase("creating");
        setFailure("");

        try {
            const res = await apiCreateLivenessLink({
                historyId,
                documentSettingId,
                fieldSettingId,
                // Fresh key per attempt — there is no server-side fallback and a
                // consumed key is rejected.
                idempotencyKey: newIdempotencyKey(),
            });

            if (res?.status === "failed") {
                const msg = errText(res, t("accountVerification.liveness.error.startFailed", {
                defaultValue: "Could not start the check. Please try again.",
            }));

                // Already passed: the field is final, so reconcile rather than error.
                if (msg.toLowerCase().includes("already accepted")) {
                    captureWindow?.close();
                    await readResult();
                    return;
                }

                captureWindow?.close();
                setPhase("idle");
                setNotificationMessage({
                    status: "error",
                    title: "",
                    msg,
                    key: "liveness_link_error",
                });
                return;
            }

            const url = res?.data?.verificationUrl;
            if (!url) {
                captureWindow?.close();
                setPhase("idle");
                setNotificationMessage({
                    status: "error",
                    title: "",
                    msg: t("accountVerification.liveness.error.noLink", {
                        defaultValue: "No verification link was returned. Please try again.",
                    }),
                    key: "liveness_link_error",
                });
                return;
            }

            // Remember the attempt BEFORE handing off, so a reload can resume it.
            // The origin is kept to match the completion message after a reload.
            let captureOrigin = "";
            try {
                captureOrigin = new URL(url).origin;
            } catch {
                /* a URL we cannot parse is one we cannot verify messages from */
            }
            writeAttempt(historyId, fieldSettingId, {
                verificationId: res?.data?.verificationId || null,
                expiresAt: res?.data?.expiresAt || null,
                origin: captureOrigin,
            });
            setHasAttempt(true);

            // No noopener/noreferrer, deliberately: the capture tab returns by
            // posting to `window.opener` and matching our referrer against the
            // tenant's return origins. Either flag strands the user on a finished
            // capture page. It is our own service, from a link just minted here.
            // Navigating a window we opened keeps both. Opened untouched: the
            // link already carries its display parameters, and its token lives
            // in the fragment.
            if (captureWindow && !captureWindow.closed) {
                // replace(), not href: Back should not land on the placeholder.
                captureWindow.location.replace(url);
            } else {
                // Refused. The link is live and remembered, so hand it over as
                // an anchor rather than burn it — see `popupBlockedNotice`.
                setPopupBlockedUrl(url);
            }
            // Nothing to ask for yet — the capture has not even started. The
            // return-to-tab listener takes it from here.
            setPhase("awaiting");
        } catch (e) {
            captureWindow?.close();
            console.error("liveness link create failed", e);
            setPhase("idle");
            setNotificationMessage({
                status: "error",
                title: "",
                msg: t("accountVerification.liveness.error.startFailed", {
                    defaultValue: "Could not start the check. Please try again.",
                }),
                key: "liveness_link_error",
            });
        }
    };

    const handleManualCheck = async () => {
        setManualChecking(true);
        try {
            const terminal = await readResult();
            // Still nothing — leave the field waiting rather than claiming a verdict.
            if (!terminal && mounted.current) setPhase("stalled");
        } catch (e) {
            console.error("liveness manual check failed", e);
        } finally {
            if (mounted.current) setManualChecking(false);
        }
    };

    const isAccepted = phase === "accepted";
    const isBusy = phase === "creating";
    const isWatching = phase === "awaiting";

    const statusTag = isAccepted ? (
        <Tag color="success" bordered={false} icon={<CheckCircleFilled/>}>
            {t("accountVerification.liveness.status.verified", {defaultValue: "Verified"})}
        </Tag>
    ) : phase === "failed" ? (
        <Tag color="error" bordered={false} icon={<WarningFilled/>}>
            {t("accountVerification.liveness.status.notVerified", {defaultValue: "Not verified"})}
        </Tag>
    ) : isWatching ? (
        <Tag color="processing" bordered={false} icon={<SyncOutlined spin/>}>
            {t("accountVerification.liveness.status.waiting", {defaultValue: "Waiting"})}
        </Tag>
    ) : phase === "stalled" ? (
        <Tag color="warning" bordered={false}>
            {t("accountVerification.liveness.status.unconfirmed", {defaultValue: "Unconfirmed"})}
        </Tag>
    ) : null;

    const checkStatusLabel = t("accountVerification.liveness.action.checkStatus", {
        defaultValue: "Check status",
    });

    // Idle has NO fallback copy — the document's description or nothing. Other
    // phases report something that happened, so they keep their text.
    const helperText =
        isAccepted ? t("accountVerification.liveness.helper.accepted", {
                defaultValue: "Your identity has been confirmed.",
            })
            : phase === "failed" ? failure
                : isWatching ? t("accountVerification.liveness.helper.awaiting", {
                        defaultValue: "Finish the check in the other tab, then come back here — this updates when you return, or press Check status.",
                    })
                    : phase === "stalled" ? t("accountVerification.liveness.helper.stalled", {
                            defaultValue: "We haven't heard back yet. Press Check status, or start over.",
                        })
                        : description;

    // "Start" would be misleading while an attempt is live — pressing it abandons
    // the open link and issues a new one.
    const startLabel =
        phase === "failed" || phase === "stalled"
            ? t("accountVerification.liveness.action.tryAgain", {defaultValue: "Try again"})
            : hasAttempt
                ? t("accountVerification.liveness.action.startOver", {defaultValue: "Start over"})
                : t("accountVerification.liveness.action.start", {defaultValue: "Start"});

    // Shown for the whole life of an attempt — including after a reload — so the
    // user can pull the verdict on demand instead of waiting for the next poll.
    const showSync = !isAccepted && (hasAttempt || isWatching || phase === "stalled");

    // layout_2 themes its own buttons: antd `primary` renders there as a muted
    // gold that reads as disabled. `secondary_btn` is what its cards use.
    const isLayout2 = layout === "layout_2";

    // Accepted is final: link-create refuses a new link, so never offer one.
    const actions = (
        <>
            {showSync && (
                <Tooltip title={checkStatusLabel}>
                    <Button
                        className={layout === "layout_3" ? "!p-1.5" : undefined}
                        type={isLayout2 ? "link" : undefined}
                        icon={<SyncOutlined spin={manualChecking}/>}
                        onClick={handleManualCheck}
                        loading={manualChecking}
                        disabled={readOnly || isBusy}
                    >
                        {layout === "layout_3" || isLayout2 ? null : checkStatusLabel}
                    </Button>
                </Tooltip>
            )}
            {!isAccepted && (
                <Button
                    className={isLayout2 ? "secondary_btn" : undefined}
                    type={isLayout2 ? undefined : (hasAttempt ? "default" : "primary")}
                    loading={isBusy}
                    disabled={readOnly || isBusy}
                    onClick={handleStart}
                    icon={<ArrowRightOutlined/>}
                >
                    {startLabel}
                </Button>
            )}
        </>
    );

    // Only on the blocked path — a direct click on an anchor is a fresh gesture
    // no blocker intercepts, so browsers that behaved never see these steps.
    const popupBlockedNotice = popupBlockedUrl ? (
        <p style={{marginTop: 8, marginBottom: 0, fontSize: 12, lineHeight: 1.5, color: "var(--warning-text-color, #faad14)"}}>
            {/* rel="opener" is required: target="_blank" implies rel="noopener"
                in Safari 12.1+ / Chrome 88+, and the capture tab reports back
                through window.opener. Our own service, from a link minted here. */}
            {/* eslint-disable-next-line react/jsx-no-target-blank */}
            <a href={popupBlockedUrl} target="_blank" rel="opener" onClick={() => setPopupBlockedUrl("")}>
                {t("accountVerification.liveness.action.openManually", {
                    defaultValue: "Open the verification page",
                })}
            </a>
            {" — "}
            {t("accountVerification.liveness.error.popupBlocked", {
                defaultValue: "your browser blocked the verification window. Use the link above, or allow pop-ups for this site (Safari: Settings ▸ Websites ▸ Pop-up Windows ▸ Allow).",
            })}
        </p>
    ) : null;

    const renderLayout3 = () => (
        <div className="liveness-card-l3">
            <div className="liveness-l3-icon-col">
                <div className={`liveness-l3-icon-wrap${isAccepted ? " is-verified" : ""}`}>
                    {isAccepted ? <CheckCircleFilled className="liveness-l3-icon"/> :
                        <ScanOutlined className="liveness-l3-icon"/>}
                </div>
            </div>

            <div className="liveness-l3-body">
                <div className="liveness-l3-title-row">
                    <span className="liveness-l3-title">{isRequired && <span className={"danger-text me-1 text-md"}>*</span>}{label || t("accountVerification.liveness.title", {defaultValue: "Liveness check"})}</span>
                    {statusTag}
                </div>
                {/* Skipped entirely when there is nothing to say — an empty <p>
                    still takes its margin and pushes the card out of shape. */}
                {helperText && (
                    <p className={`liveness-l3-desc${phase === "failed" ? " is-error" : ""}`}>{helperText}</p>
                )}
                {popupBlockedNotice}
            </div>

            <div className="liveness-l3-actions">{actions}</div>
        </div>
    );

    const renderDefault = () => (
        <div className="questionnaire-card">
            {/* Wrapping stays ON — this markup also renders on mobile, where the
                actions must drop below the text. minWidth 0 stops a long title
                pushing them off the card. */}
            <Row justify={"space-between"} align={"middle"} style={{rowGap: 8}}>
                <div style={{minWidth: 0}}>
                    <span className="flex items-center gap-2" style={{flexWrap: "wrap"}}>
                        <h6 style={{marginBottom: 0}}>
                            {isRequired && <span className={"danger-text me-1 text-md"}>*</span>}
                            {label || t("accountVerification.liveness.title", {defaultValue: "Liveness check"})}
                        </h6>
                        {statusTag}
                    </span>
                    {/* No empty paragraph when the author left the description blank. */}
                    {helperText && <p style={{marginTop: 4, marginBottom: 0}}><small>{helperText}</small></p>}
                    {popupBlockedNotice}
                </div>
                <div className="flex gap-2 items-center">{actions}</div>
            </Row>
        </div>
    );

    // Missing ids means the layout hasn't threaded historyId/documentSettingId into
    // renderFormFields. Say so instead of rendering a Start button that no-ops.
    if (!historyId || !documentSettingId || !fieldSettingId) {
        return (
            <div className={layout === "layout_3" ? "liveness-card-l3" : "questionnaire-card"}>
                <WarningFilled style={{color: "var(--danger-text-color, #ff4d4f)"}}/>
                <span style={{marginInlineStart: 8}}>
                    {t("accountVerification.liveness.error.unavailable", {
                        defaultValue: "This check isn't available right now. Please contact support.",
                    })}
                </span>
            </div>
        );
    }

    if (phase === "checking") {
        return (
            <div className={layout === "layout_3" ? "liveness-card-l3" : "questionnaire-card"}>
                <SyncOutlined spin/> <span style={{marginInlineStart: 8}}>
                    {t("accountVerification.liveness.checking", {defaultValue: "Checking…"})}
                </span>
            </div>
        );
    }

    return layout === "layout_3" ? renderLayout3() : renderDefault();
};
